import { ActionType } from '@unifyapps/defs/types/action';
import type { BlockStateUnionType, BlockType } from '@unifyapps/defs/types/block';
import _reduce from 'lodash/reduce';
import type { OnActionType } from '../../../components/ActionsProvider/context';
import type BlocksRegistry from '../../../components/RegistryProvider/BlocksRegistry';
import type { BlockControlMethod } from '../../../components/BlockDefinition';
import { getOriginalBlockId, isRuntimeBlockId } from '../../../utils/runTimeBlocks';

const createBlockMethodFunction = (
  blockId: string,
  targetBlockId: string,
  methodIds: string[],
  onAction?: OnActionType,
) => {
  if (methodIds.length === 0) {
    return {};
  }

  const methodFunctions = methodIds.reduce((acc, methodId) => {
    acc[methodId] = (methodPayload: Record<string, unknown>) => {
      if (!onAction) return;

      void onAction({
        blockId: targetBlockId,
        action: {
          actionType: ActionType.ControlBlockMethod,
          payload: {
            blockId,
            methodName: methodId,
            methodPayload,
          },
        },
        actionContext: {},
      });
    };
    return acc;
  }, {});

  return methodFunctions;
};

const createDataSourceMethodFunction = (
  dataSourceId: string,
  methodName: string,
  onAction?: OnActionType,
) => {
  const methodFunction = () => {
    if (!onAction) return;

    void onAction({
      action: {
        actionType: ActionType.ControlDataSource,
        payload: {
          method: methodName,
          dataSourceId,
        },
      },
      actionContext: {},
    });
  };

  return methodFunction;
};

const createPageVariableMethodFunction = (
  pageVariableId: string,
  methodName: string,
  onAction?: OnActionType,
) => {
  const methodFunction = (value: unknown) => {
    if (!onAction) return;

    void onAction({
      action: {
        actionType: ActionType.SetPageVariable,
        payload: {
          variableId: pageVariableId,
          operationDetails: {
            operation: 'SET',
            value,
          },
        },
      },
      actionContext: {},
    });
  };

  return methodFunction;
};

export const getPageVariablesMethods = ({
  pageVariableIds,
  onAction,
}: {
  pageVariableIds: string[];
  onAction?: OnActionType;
}) => {
  return _reduce(
    pageVariableIds,
    (acc, pageVariableId) => {
      acc[pageVariableId] = {
        setValue: createPageVariableMethodFunction(pageVariableId, 'setValue', onAction),
      };
      return acc;
    },
    {},
  );
};

export const getBlocksMethods = ({
  blocks,
  blockStates,
  onAction,
  blockRegistry,
}: {
  blockStates: Record<string, BlockStateUnionType | undefined>;
  blocks: Record<string, BlockType | undefined>;
  onAction?: OnActionType;
  blockRegistry: BlocksRegistry;
}): Record<string, BlockControlMethod[] | undefined> => {
  const result = _reduce(
    blockStates,
    (acc, block, key) => {
      if (!block) return acc;

      const blockId = key;

      const isRuntimeBlock = isRuntimeBlockId(blockId);
      const originalBlockId = getOriginalBlockId(blockId);

      // if it is runTimeBlock then we don't have componentType stored with blockState
      const componentType = isRuntimeBlock
        ? blockStates[originalBlockId]?.componentType
        : block.componentType;

      if (!componentType) {
        console.warn('Component type is not defined for block', block, key);
        return acc;
      }

      const originalBlock = blocks[originalBlockId];

      if (!originalBlock) {
        console.warn('Block not found in blocks', block, key);
        return acc;
      }

      const methods = blockRegistry.getBlockControlMethods(componentType, originalBlock) as
        | BlockControlMethod[]
        | undefined;

      if (!methods) {
        console.debug('Methods are not defined for block', block, key);
        return acc;
      }

      acc[key] = createBlockMethodFunction(
        block.id,
        key,
        methods.map((method) => method.id),
        onAction,
      );

      return acc;
    },
    {},
  );

  return result;
};

export const getDataSourcesMethods = ({
  dataSourceIds,
  onAction,
}: {
  dataSourceIds: string[];
  onAction?: OnActionType;
}) => {
  return _reduce(
    dataSourceIds,
    (acc, dataSourceId) => {
      acc[dataSourceId] = {
        trigger: createDataSourceMethodFunction(dataSourceId, 'trigger', onAction),
      };

      return acc;
    },
    {},
  );
};
